import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import generateTTS from '../services/tts';
import ttsCache from '../services/ttsCache';

export interface TTSContextType {
  /** Currently playing block ID */
  currentPlayingBlockId: string | null;
  /** Whether audio is currently loading */
  isLoading: boolean;
  /** Loading block ID (for showing spinner on specific block) */
  loadingBlockId: string | null;
  /** Play TTS for a specific block */
  play: (blockId: string, text: string) => Promise<void>;
  /** Pause current playback */
  pause: () => void;
  /** Resume paused playback */
  resume: () => void;
  /** Stop current playback completely */
  stop: () => void;
  /** Check if a specific block is currently playing */
  isPlaying: (blockId: string) => boolean;
  /** Check if playback is paused */
  isPaused: boolean;
  /** Error state */
  error: string | null;
  /** Clear error */
  clearError: () => void;
}

const TTSContext = createContext<TTSContextType | undefined>(undefined);

interface TTSProviderProps {
  children: ReactNode;
}

export const TTSProvider: React.FC<TTSProviderProps> = ({ children }) => {
  const [currentPlayingBlockId, setCurrentPlayingBlockId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingBlockId, setLoadingBlockId] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Audio refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const pausedAtRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);

  // Get or create AudioContext
  const getAudioContext = useCallback((): AudioContext => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Stop any existing playback
  const stopPlayback = useCallback(() => {
    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.stop();
      } catch (e) {
        // Ignore errors if already stopped
      }
      audioSourceRef.current = null;
    }
    setCurrentPlayingBlockId(null);
    setIsPaused(false);
    pausedAtRef.current = 0;
    startTimeRef.current = 0;
    audioBufferRef.current = null;
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopPlayback();
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, [stopPlayback]);

  const play = useCallback(
    async (blockId: string, text: string) => {
      if (!text || text.trim().length === 0) {
        console.warn('No text to convert to speech');
        return;
      }

      // Stop any existing playback
      stopPlayback();
      setError(null);
      setIsLoading(true);
      setLoadingBlockId(blockId);

      try {
        const audioContext = getAudioContext();

        // Resume audio context if suspended (browser autoplay policy)
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        let arrayBuffer: ArrayBuffer;

        // Check cache first
        if (ttsCache.has(text)) {
          const cachedAudio = ttsCache.get(text);
          if (cachedAudio) {
            // Clone the ArrayBuffer since it can only be decoded once
            arrayBuffer = cachedAudio.slice(0);
          } else {
            throw new Error('Cache returned null');
          }
        } else {
          // Fetch from API
          arrayBuffer = await generateTTS(text);
          // Store in cache (store a copy)
          ttsCache.set(text, arrayBuffer.slice(0));
        }

        // Decode audio data
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        audioBufferRef.current = audioBuffer;

        // Create audio source
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContext.destination);

        // Handle playback end
        source.onended = () => {
          setCurrentPlayingBlockId(null);
          setIsPaused(false);
          audioSourceRef.current = null;
          pausedAtRef.current = 0;
          startTimeRef.current = 0;
        };

        // Start playback
        audioSourceRef.current = source;
        startTimeRef.current = audioContext.currentTime;
        source.start(0);
        setCurrentPlayingBlockId(blockId);
      } catch (err) {
        console.error('Failed to play TTS audio:', err);
        setError('Failed to play audio. Please try again.');
        setCurrentPlayingBlockId(null);
      } finally {
        setIsLoading(false);
        setLoadingBlockId(null);
      }
    },
    [getAudioContext, stopPlayback]
  );

  const pause = useCallback(() => {
    const audioContext = audioContextRef.current;
    const source = audioSourceRef.current;

    if (audioContext && source && currentPlayingBlockId) {
      // Calculate how far we are in playback
      pausedAtRef.current = audioContext.currentTime - startTimeRef.current;

      try {
        source.stop();
      } catch (e) {
        // Ignore
      }
      audioSourceRef.current = null;
      setIsPaused(true);
    }
  }, [currentPlayingBlockId]);

  const resume = useCallback(() => {
    const audioContext = audioContextRef.current;
    const audioBuffer = audioBufferRef.current;

    if (audioContext && audioBuffer && isPaused && currentPlayingBlockId) {
      // Resume audio context if suspended
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      // Create new source and start from paused position
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);

      source.onended = () => {
        setCurrentPlayingBlockId(null);
        setIsPaused(false);
        audioSourceRef.current = null;
        pausedAtRef.current = 0;
        startTimeRef.current = 0;
      };

      audioSourceRef.current = source;
      startTimeRef.current = audioContext.currentTime - pausedAtRef.current;
      source.start(0, pausedAtRef.current);
      setIsPaused(false);
    }
  }, [isPaused, currentPlayingBlockId]);

  const stop = useCallback(() => {
    stopPlayback();
  }, [stopPlayback]);

  const isPlaying = useCallback(
    (blockId: string): boolean => {
      return currentPlayingBlockId === blockId && !isPaused;
    },
    [currentPlayingBlockId, isPaused]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const contextValue: TTSContextType = {
    currentPlayingBlockId,
    isLoading,
    loadingBlockId,
    play,
    pause,
    resume,
    stop,
    isPlaying,
    isPaused,
    error,
    clearError
  };

  return <TTSContext.Provider value={contextValue}>{children}</TTSContext.Provider>;
};

export const useTTS = (): TTSContextType => {
  const context = useContext(TTSContext);
  if (context === undefined) {
    throw new Error('useTTS must be used within a TTSProvider');
  }
  return context;
};
